import { useState } from "react";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

export const KIND_LABEL = {
  index: "Share indices",
  equity: "Shares",
  bond: "Government bonds",
  fx: "Currencies",
  deposit: "Deposits",
};

export const KindFilter = ({ value, onChange, testid = "mkt-kinds" }) => (
  <div data-testid={testid} className="flex gap-2 scroll-x overflow-x-auto pb-1">
    {[["all", "Everything"], ...Object.entries(KIND_LABEL)].map(([key, label]) => (
      <button key={key} data-testid={`mkt-kind-${key}`} onClick={() => onChange(key)}
        className={`whitespace-nowrap border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
          value === key ? "border-[#002FA7] bg-[#002FA7] text-white"
            : "border-border bg-white text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"}`}>
        {label}
      </button>
    ))}
  </div>
);

const Spark = ({ history }) => {
  const points = (history || []).map((h) => Number(h.value)).filter((n) => !Number.isNaN(n));
  if (points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const path = points
    .map((v, i) => `${(i / (points.length - 1)) * 100},${28 - ((v - min) / span) * 26}`)
    .join(" ");
  const rising = points[points.length - 1] >= points[0];
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="mt-3 h-8 w-full" aria-hidden="true">
      <polyline points={path} fill="none" stroke={rising ? "#0F7A3D" : "#B3241B"} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const QuoteCard = ({ row, index, onQuote, onRemove, canEdit }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.value ?? "");
  const [change, setChange] = useState(row.change_pct ?? "");
  const move = row.change_pct;
  const Icon = move == null ? Minus : move >= 0 ? TrendUp : TrendDown;
  const tone = move == null ? "text-slate-400" : move >= 0 ? "text-[#0B5C2E]" : "text-[#B3241B]";

  return (
    <div data-testid={`mkt-card-${index}`} className="border border-border bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-[#0F172A]">{row.name}</p>
          <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
            {KIND_LABEL[row.kind]}{row.symbol ? ` · ${row.symbol}` : ""}{row.country ? ` · ${row.country}` : ""}
          </p>
        </div>
        <Icon size={16} className={tone} />
      </div>
      <p className="mt-3 text-2xl font-bold text-[#0F172A]" data-testid={`mkt-value-${index}`}>
        {row.value != null ? Number(row.value).toLocaleString(undefined, { maximumFractionDigits: 4 }) : "—"}
        {row.kind === "bond" && row.value != null ? "%" : ""}
      </p>
      {move != null && <p className={`text-sm font-semibold ${tone}`}>{move > 0 ? "+" : ""}{move}%</p>}
      <Spark history={row.history} />
      <p className="mt-2 text-[11px] text-slate-400">
        {row.as_of ? `As of ${new Date(row.as_of).toLocaleString()}` : "No price yet"}
        {row.stale ? " · out of date" : ""}
      </p>
      {canEdit && (
        <div className="mt-3">
          {editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid={`mkt-edit-value-${index}`} type="number" value={value} onChange={(e) => setValue(e.target.value)}
                className="w-24 border border-border px-2 py-1.5 text-base md:text-sm" placeholder="Price" />
              <input data-testid={`mkt-edit-change-${index}`} type="number" value={change} onChange={(e) => setChange(e.target.value)}
                className="w-20 border border-border px-2 py-1.5 text-base md:text-sm" placeholder="%" />
              <button data-testid={`mkt-edit-save-${index}`} className="bg-[#002FA7] px-3 py-1.5 text-xs font-semibold text-white"
                onClick={async () => { await onQuote(row, value, change); setEditing(false); }}>Save</button>
              <button data-testid={`mkt-edit-cancel-${index}`} className="text-xs text-slate-500" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button data-testid={`mkt-edit-${index}`} className="text-xs font-semibold text-[#002FA7]" onClick={() => setEditing(true)}>
                Set the price
              </button>
              <button data-testid={`mkt-remove-${index}`} className="text-xs font-semibold text-slate-500 hover:text-[#B3241B]" onClick={() => onRemove(row)}>
                Remove
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
